import React from "react";
import {
  Shield,
  Users,
  Building2,
  Layers,
  Sparkles,
  Activity,
  TrendingUp,
} from "lucide-react";
import { SCOPE_LEVEL_OPTIONS } from "../../../config/featureGroups";

function StatCard({ label, value, hint, icon: Icon, tone = "red" }) {
  const tones = {
    red: "border-red-100 from-red-50/80 to-white text-red-800",
    blue: "border-blue-100 from-blue-50/80 to-white text-blue-800",
    emerald: "border-emerald-100 from-emerald-50/80 to-white text-emerald-800",
    amber: "border-amber-100 from-amber-50/80 to-white text-amber-800",
  };
  const iconTones = {
    red: "bg-red-600",
    blue: "bg-blue-600",
    emerald: "bg-emerald-600",
    amber: "bg-amber-500",
  };

  return (
    <div className={`rounded-2xl border bg-gradient-to-br p-4 shadow-sm ${tones[tone]?.split(" ").slice(0, 2).join(" ")}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">{label}</p>
          <p className={`mt-1.5 text-2xl font-black ${tones[tone]?.split(" ").pop()}`}>{value ?? "—"}</p>
          {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
        </div>
        {Icon && (
          <div className={`p-2 rounded-xl text-white shadow-sm ${iconTones[tone]}`}>
            <Icon size={18} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function UsageRoleStatsBar({ stats, loading }) {
  if (loading && !stats) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3 animate-pulse">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="h-24 rounded-2xl bg-gray-100" />
        ))}
      </div>
    );
  }

  if (!stats) return null;

  const scopeMap = Object.fromEntries(SCOPE_LEVEL_OPTIONS.map((o) => [o.value, o.label]));
  const activity7d =
    (stats.activity?.created_7d || 0) + (stats.activity?.updated_7d || 0);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatCard
          label="Quyền đang dùng"
          value={stats.total_active ?? 0}
          hint={stats.total_inactive ? `${stats.total_inactive} ngưng` : undefined}
          icon={Shield}
          tone="red"
        />
        <StatCard
          label="NV đã gán quyền"
          value={stats.employees_assigned ?? 0}
          hint="Tài khoản có quyền sử dụng"
          icon={Users}
          tone="blue"
        />
        <StatCard
          label="Chi nhánh"
          value={stats.branches_with_roles ?? 0}
          hint={`${stats.departments_with_roles ?? 0} bộ phận`}
          icon={Building2}
          tone="emerald"
        />
        <StatCard
          label="Mẫu hệ thống"
          value={stats.preset_count ?? 0}
          hint="Preset mặc định"
          icon={Sparkles}
          tone="amber"
        />
        <StatCard
          label="Hoạt động 7 ngày"
          value={activity7d}
          hint={`${stats.activity?.created_7d ?? 0} thêm · ${stats.activity?.updated_7d ?? 0} sửa`}
          icon={TrendingUp}
          tone="blue"
        />
        <StatCard
          label="Thao tác 24h"
          value={stats.activity?.logs_24h ?? 0}
          hint="Ghi nhận trong nhật ký"
          icon={Activity}
          tone="red"
        />
      </div>

      {(stats.by_scope?.length > 0 || stats.top_branches?.length > 0) && (
        <div className="flex flex-wrap gap-4 text-xs">
          {stats.by_scope?.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                <Layers size={12} /> Phạm vi:
              </span>
              {stats.by_scope.map((s) => (
                <span
                  key={s.scope_level}
                  className="px-2 py-0.5 rounded-lg bg-white border border-gray-200 text-gray-700 font-medium"
                >
                  {scopeMap[s.scope_level] || s.scope_level}: {s.count}
                </span>
              ))}
            </div>
          )}
          {stats.top_branches?.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-bold text-gray-400 uppercase tracking-wider">Top CN:</span>
              {stats.top_branches.map((b) => (
                <span
                  key={b.branch_name}
                  className="px-2 py-0.5 rounded-lg bg-white border border-gray-200 text-gray-700 font-medium"
                >
                  {b.branch_name} ({b.count})
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
